import { useMsal } from '@azure/msal-react';
import {
    Avatar,
    Box,
    Divider,
    IconButton,
    Link,
    Menu,
    Typography,
} from '@mui/material';
import { Cog, LayoutDashboard, LogOut } from 'lucide-react';
import { useState } from 'react';
import { useRecoilValue } from 'recoil';
import { MenuItem } from '~/components/Menu';
import { checkIsInSessionRouteWithoutRouter } from '~/hooks/session/useIsInSessionRoute';
import { sessionDataAtom } from '~/state/atoms/sessionAtoms';
import AppConfig from '~/utils/appconfig';

export default function ExternalLinksMenu() {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { instance } = useMsal();
    const sessionData = useRecoilValue(sessionDataAtom);

    const handleLogout = () => {
        instance.logoutRedirect({
            postLogoutRedirectUri: '/logout',
        });
    };

    const handleClose = () => setAnchorEl(null);

    const isInConnectorRoute = checkIsInSessionRouteWithoutRouter();
    const activeAccount = instance.getActiveAccount();
    const userName = isInConnectorRoute
        ? sessionData?.username
        : activeAccount?.name || activeAccount?.username;
    const userInitial = userName ? userName.charAt(0).toUpperCase() : '';

    return (
        <>
            <IconButton
                aria-label="open-external-links-menu"
                aria-haspopup="true"
                onClick={(event) => setAnchorEl(event.currentTarget)}
                data-testid="external-links-button"
            >
                {userInitial ? (
                    <Avatar
                        sx={(theme) => ({
                            bgcolor: theme.palette.primary.main,
                            width: 24,
                            height: 24,
                            fontSize: 14,
                            color: 'white',
                        })}
                    >
                        {userInitial}
                    </Avatar>
                ) : (
                    <Avatar sx={{ width: 24, height: 24 }} />
                )}
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    paper: {
                        sx: {
                            background: 'rgba(4, 30, 52, 0.65)',
                            backdropFilter: 'blur(5px)',
                            WebkitBackdropFilter: 'blur(5px)',
                            border: '1px solid rgba(0, 0, 0, 0.1)',
                            p: 1.6,
                            borderRadius: 2,
                            color: 'white',
                            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.27)',
                        },
                    },
                }}
            >
                <Box display="flex" flexDirection="column" width={160}>
                    <Link
                        href={AppConfig.dashboardUrl}
                        sx={{ textDecoration: 'none', color: 'inherit' }}
                        target="_blank"
                        rel="noreferrer"
                    >
                        <MenuItem
                            label="Dashboard"
                            onClick={handleClose}
                            icon={<LayoutDashboard size={16} />}
                        />
                    </Link>
                    <Link
                        href={AppConfig.configUrl}
                        sx={{ textDecoration: 'none', color: 'inherit' }}
                        target="_blank"
                        rel="noreferrer"
                    >
                        <MenuItem
                            label="Configuration"
                            onClick={handleClose}
                            icon={<Cog size={16} />}
                        />
                    </Link>

                    <MenuItem
                        label="Log out"
                        onClick={handleLogout}
                        icon={<LogOut size={16} />}
                    />
                    <Divider
                        sx={{
                            mt: 0,
                            m: 0,
                            borderColor: 'rgba(255, 255, 255, 0.1)',
                        }}
                    />
                    <Box display="flex" alignItems="center" gap={1}>
                        <Avatar
                            sx={(theme) => ({
                                bgcolor: theme.palette.primary.main,
                                width: 24,
                                height: 24,
                                fontSize: 14,
                                color: 'white',
                            })}
                        >
                            {userInitial}
                        </Avatar>
                        <Box>
                            <Typography fontSize={12} variant="body2">
                                {userName || 'User'}
                            </Typography>
                            {isInConnectorRoute ? (
                                <Typography fontSize={10} variant="body2">
                                    Workstation session
                                </Typography>
                            ) : (
                                <Typography fontSize={10} variant="body2">
                                    {activeAccount?.username}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Menu>
        </>
    );
}
